import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { themeClasses, Button } from '@member-portal/ui';
import { useAuth } from '@/contexts/AuthContext';
import MembersOnlyOverlay from '@/components/MembersOnlyOverlay';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
  membersOnly?: boolean;
}

const faqSections: FAQSection[] = [
  {
    title: 'General',
    membersOnly: false,
    items: [
      {
        question: 'Who can use the FHxD Platform?',
        answer: 'Any accredited travel advisor can use FHxD.',
      },
      {
        question: 'How do I use the FHxD Platform?',
        answer: 'Click the sign up button, fill in the form and click submit. We\'ll send you a user name and password so you can access the site. Then browse over 400 preferred partners to learn more about the partner, special benefits, promotions and more. When you are ready to book, click the request booking button, fill in the form, and a FHxD booking agent will get in touch to fulfill the request.',
      },
      {
        question: 'Does it cost anything to use FHxD?',
        answer: 'There is no cost and no commitment to use the FHxD platform.',
      },
      {
        question: 'Where can I learn more?',
        answer: 'Sign up, then log in and scroll to the footer of the website. Click FAQs to open a comprehensive set of FAQs on a variety of subjects.',
      },
    ],
  },
  {
    title: 'Privacy / Confidentiality',
    membersOnly: false,
    items: [
      {
        question: 'How does FHxD use my agency information?',
        answer: 'Maintaining your own agency identity and client relationship is of primary importance to Four Hundred by Design. For each booking, you are noted as the agent of record and your contact information is provided to the partner. Once the booking is confirmed, you work directly with us and maintain your relationship with the partner to fulfill all of your client\'s needs.',
      },
      {
        question: 'How does FHxD use my client\'s information?',
        answer: 'Client information is used on a one-time transactional basis for booking purposes only. Your clients will never receive mailings, marketing, or any other communication from Four Hundred by Design.',
      },
    ],
  },
  {
    title: 'Partner Collection',
    membersOnly: true,
    items: [
      {
        question: 'How are Four Hundred by Design properties selected?',
        answer: 'Each property has been selected for providing the finest accommodations, service levels and quality standards. Many villas and residences are off-market and exclusively available to Four Hundred by Design.',
      },
      {
        question: 'Can I make a recommendation on a property I\'d like to see added to the platform?',
        answer: 'Yes. We always encourage suggestions and recommendations. Use the "I have a question" section of the Request Booking form to send us information about a property you\'re interested in seeing on the platform.',
      },
      {
        question: 'What if I want to book a property or room type that\'s not listed?',
        answer: 'You can request a booking only for the featured properties and for the accommodation types listed on each property\'s detail page via the FHxD platform.',
      },
      {
        question: 'How can I recommend a Villa or Hotel to FHxD that\'s not currently listed?',
        answer: 'We always encourage suggestions and recommendations. Use the "I have a question" section of the Request Booking form to send us information about a property or room type you\'re interested in seeing on the FHxD platform.',
      },
      {
        question: 'Are the rates per person or for the entire villa?',
        answer: 'Our rates are for personal use of the entire villa. Events including parties, weddings and other large gatherings require written permission. Some larger villas offer lower rates for use of fewer bedrooms within the property. Villas may not be occupied by more guests than specified in the rental agreement.',
      },
      {
        question: 'What is the required length of stay?',
        answer: 'Normally a 4 to 7-night stay is required. Many villas do accept shorter stays, especially if it is close to your departure date. During the Christmas and New Year\'s Holiday period, most villas require a minimum of 10-14 nights.',
      },
      {
        question: 'Does a weekly rental always start on the same day each week?',
        answer: 'Generally, in most destinations, clients can arrive on any day of the week, if the villa is available. In Europe, in particular France and Italy, the normal arrival and departure day is Saturday.',
      },
      {
        question: 'Do my clients have to pay full rental if we book a villa with more bedrooms than we need?',
        answer: 'Not necessarily. Many villas can be rented at a reduced rate when you do not use all the bedrooms. Use the "I have a question" section of the Booking Request form to inquire.',
      },
      {
        question: 'If we do not book all the bedrooms in a villa will someone else (i.e. strangers) be using those bedrooms and sharing the house?',
        answer: 'You are renting the entire villa and you never have to share a villa with anyone other than those in your party.',
      },
      {
        question: 'Are transfers included?',
        answer: 'Generally speaking, transfers are not included or needed. We highly recommend a car rental so clients can move freely around their chosen destination.',
      },
      {
        question: 'Do all FHxD villas include staff?',
        answer: 'Please see the Staff and Services section of the Villa detail page. If a staff or service is listed, it is included in the price for that Villa.',
      },
    ],
  },
  {
    title: 'Booking Requests',
    membersOnly: true,
    items: [
      {
        question: 'How does the booking process work?',
        answer: 'After determining your client\'s needs, log in to the FHxD platform and search over 400 Hotels, Resorts, Villas, and Private Residences. Once you find a property you\'re interested in, click the Request Booking button located on every property detail page to submit questions or, fill in as much information as possible to request a booking. A member of our FHxD booking team will be in touch to answer questions, work through the details and fulfill the booking.',
      },
      {
        question: 'How will I know my booking is confirmed?',
        answer: 'Our booking team will notify you by email with a courtesy hold on the property selected. You will be sent a formal confirmation when a credit card is given as a deposit or guarantee.',
      },
      {
        question: 'Where can I see the terms and conditions for the property?',
        answer: 'General terms and conditions are listed on every property detail page. Detailed terms and conditions can be provided by our booking team during the booking process and will be provided on the booking confirmation.',
      },
      {
        question: 'I booked a property and now see the terms and conditions don\'t work for my client. Why can\'t I access those sooner?',
        answer: 'You can get terms and conditions anytime by using the Request Booking form to get in touch with our FHxD Booking Team.',
      },
      {
        question: 'How do I change or cancel a request or existing booking?',
        answer: 'Use the email provided on the booking confirmation to contact a FHxD booking agent who can assist you with making changes to your booking.',
      },
    ],
  },
  {
    title: 'Privacy / Confidentiality',
    membersOnly: true,
    items: [
      {
        question: 'What information is needed to request a booking?',
        answer: 'Click "Request Booking" to see the form that provides the required information for a booking.',
      },
      {
        question: 'Why does FHxD need my IATA # and my agency information?',
        answer: 'FHxD requires an IATA number to ensure you\'re an accredited travel advisor and agency. It\'s also how we\'ll track your bookings and pay your commissions.',
      },
      {
        question: 'How does FHxD use my information?',
        answer: 'Maintaining your own agency identity and client relationship is of primary importance to Four Hundred by Design. The platform is the connection between you and the properties for bookings. For each booking, you are noted as the agent of record and all of your contact information is provided to the partner. Once the booking is confirmed, you work directly with the partner to fulfill all your client\'s needs.',
      },
      {
        question: 'How does FHxD use my client\'s information?',
        answer: 'Client information is used on a one-time transactional basis for booking purposes only. Your clients will never receive mailings, marketing or any other communication from Four Hundred by Design.',
      },
    ],
  },
  {
    title: 'Payments, Property Terms & Conditions, Commissions',
    membersOnly: true,
    items: [
      {
        question: 'How are bookings paid for?',
        answer: 'Credit cards are required as payment for all bookings.',
      },
      {
        question: 'What is the deposit and cancellation policy for Hotels and Resorts?',
        answer: 'Deposit and cancellation policies differ for each property and booking. You can contact a FHxD booking team member at any time via the Request Booking form to get more detailed deposit and cancellation policies prior to making a booking. These policies will also be provided on the booking confirmation.\n\nPayments & Terms & Conditions: Accommodations and requests for space are subject to availability. All bookings adhere to hotel\'s payment, cancellation, deposit and other individual policies, terms and conditions.',
      },
      {
        question: 'What is the deposit and cancellation policy for Villas and Private Residences?',
        answer: 'A 50% deposit is required within 3 days to hold a reservation. Reservations are not considered firm until the deposit and rental agreement form are received. A confirmation letter is sent upon receipt of the deposit. The balance of payment is due 90 days prior to arrival date. Within 90 days of arrival, payments become non-refundable. Please note that Festive bookings are 100% non-refundable.\n\nCancellation policies differ for each property and booking. You can contact a FHxD booking team member at any time via the Request Booking form to get more detailed deposit and cancellation policies prior to making a booking. These policies will also be provided on the booking confirmation.',
      },
      {
        question: 'What commission am I eligible to receive?',
        answer: 'Receive higher earnings on every booking. Details to be communicated at time of booking by the Four Hundred by Design reservations team. From time to time, properties may run promotions with increased commissions. If promotions are available, you\'ll see a promotions box near the bottom of the property detail page.',
      },
      {
        question: 'How are commissions paid?',
        answer: 'Commissions are paid via check.',
      },
      {
        question: 'When will I receive my commission?',
        answer: 'Commission is typically paid 7-10 days after guest departure.',
      },
    ],
  },
  {
    title: 'Contact',
    membersOnly: true,
    items: [
      {
        question: 'I have questions, how do I get in touch?',
        answer: 'For questions about a property, use the Request Booking form. For questions about an existing booking, or to make changes or cancel a booking, use the email provided on the booking confirmation.',
      },
    ],
  },
];

function FAQItemComponent({ item }: { item: FAQItem }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-theme-border">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 flex items-center justify-between text-left"
      >
        <span className={`${themeClasses.title.h5} pr-4`}>{item.question}</span>
        <ChevronDownIcon
          className={`h-5 w-5 text-theme-text-muted flex-shrink-0 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-[500px] pb-6' : 'max-h-0'
        }`}
      >
        <p className={`${themeClasses.text.body} whitespace-pre-line`}>{item.answer}</p>
      </div>
    </div>
  );
}

function FAQPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <div className="fhxd-chrome-top">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/images/laguna-2.jpg)' }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-title font-normal text-3xl md:text-5xl lg:text-7xl tracking-wider uppercase text-white mb-6">
            Frequently Asked Questions
          </h1>
          <p className="font-body font-normal leading-relaxed tracking-form text-white/90">
            Everything you need to know about FHxD
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-24 bg-theme-bg">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqSections
            .filter((section) => !section.membersOnly || isAuthenticated)
            .map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-16 last:mb-0">
              <h2 className={`${themeClasses.title.h3} mb-8`}>{section.title}</h2>
              <div>
                {section.items.map((item, itemIndex) => (
                  <FAQItemComponent key={itemIndex} item={item} />
                ))}
              </div>
            </div>
          ))}

          {!isAuthenticated && (
            <div className="mb-16 last:mb-0">
              <h2 className={`${themeClasses.title.h3} mb-8`}>For Advisors</h2>
              <div className="relative h-[280px] overflow-hidden">
                <div className="select-none pointer-events-none" aria-hidden="true">
                  {faqSections
                    .filter((section) => section.membersOnly)
                    .flatMap((section) => section.items)
                    .slice(0, 4)
                    .map((item, itemIndex) => (
                      <FAQItemComponent key={itemIndex} item={item} />
                    ))}
                </div>
                <MembersOnlyOverlay
                  title="For Advisors"
                  message="Log in or sign up to view these FAQs."
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-theme-bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`${themeClasses.title.h3} mb-6`}>
            Still Have Questions?
          </h2>
          <p className={`${themeClasses.text.body} mb-8`}>
            Contact us and we'll be happy to help.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button variant="secondary" onClick={() => window.location.href = 'mailto:info@fourhundredbydesign.com'}>
              Contact Us
            </Button>
            <Button variant="primary" onClick={() => navigate('/signup')}>
              Advisor Sign Up
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FAQPage;
